import { useState, useRef, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";

import { authSelector, personsSelector } from "../reducers";

import { getLocaleText } from "../actions/datagrid";
import { getAllPersons } from "../actions/persons/personsgetall";
import DefaultDatePicker from "../common/datepicker/DefaultDatePicker.jsx";

import {
  dateDiff,
  dateFormatter,
  dateGetter,
  emptyFormatter,
  eSalaryImage,
  localDate,
  localized,
  nextBirthDay,
  socSecToBirthDate,
} from "../common/utils";
import { PdfReport } from "../common/pdfreport";
import {
  contextMenu,
  customChartThemes,
  defaultColDef,
  sideBar,
  statusBar,
  gridSettings,
  saveViewState,
  loadViewState,
  restoreViewState,
} from "../common/grid";
import { restoreIconString, TemplateProfileImage } from "../common/icons";

const viewStateKey = "anniversaryViewState";

export default function AnniversaryView() {
  const dispatch = useDispatch();
  const { companyName, eSalaryCompanyId } = useSelector(authSelector);
  const persons = useSelector(personsSelector);
  const { t } = useTranslation();
  const prefix = "anniversaryView";

  useEffect(() => {
    dispatch(getAllPersons(true)); // active persons only
    //return () => dispatch({type:"PERSONS_UNMOUNT", payload: { persons: [] }});
  }, []);

  const [targetDate, setTargetDate] = useState(localDate());

  const [report, setReport] = useState(null);
  const closeReport = () => setReport(null);

  function calculateAge(birthday) {
    // birthday is a date
    if (birthday === null) return;
    try {
      const diff = dateDiff(targetDate, new Date(birthday));
      return diff.years;
    } catch {
      return null;
    }
  }

  const genderGetter = ({ colDef, data }) => {
    if (!data) return null;
    const value = data[colDef.field];
    return value === "M" ? t("Male", "Mies") : t("Female", "Nainen");
  };

  const gridRef = useRef();

  const gridComponents = {
    profileImage: ({ value }) => (
      <div className="profile-image">
        {value && value.length ? (
          <img className="ag-image" src={eSalaryImage(eSalaryCompanyId, value)} crossOrigin="anonymous" />
        ) : (
          <TemplateProfileImage />
        )}
      </div>
    ),
  };

  const columnDefs = [
    {
      headerName: t("Image", "Kuva"),
      field: "otherDetails.image",
      filter: false,
      sortable: false,
      suppressSizeToFit: true,
      width: 80,
      cellRenderer: "profileImage",
      valueFormatter: () => emptyFormatter,
    },
    {
      headerName: t("NumberAbbr", "Nro"),
      field: "nr",
      filter: "agNumberColumnFilter",
      suppressSizeToFit: true,
      minWidth: 115,
      maxWidth: 115,
      width: 115,
    },
    {
      headerName: t("Name", "Nimi"),
      field: "name",
    },
    {
      headerName: t("Gender", "Sukupuoli"),
      field: "gender",
      valueGetter: genderGetter,
    },
    {
      headerName: t("EmploymentStartDate", "Työsuhde alkoi"),
      cellClass: "dateType",
      field: "employmentDetails.startDate",
      filter: "agDateColumnFilter",
      valueGetter: dateGetter,
      valueFormatter: dateFormatter,
    },
    {
      headerName: t("ExperienceYears", "Kokemusvuodet"),
      field: "employmentDetails.startDate",
      filter: "agNumberColumnFilter",
      valueGetter: (params) =>
        params.data?.employmentDetails &&
        calculateAge(
          params.data.employmentDetails.startDate
            ? params.data.employmentDetails.startDate
            : params.data.employmentDetails.contractStartDate,
        ),
    },
    {
      headerName: t("Birthdate", "Syntymäaika"),
      cellClass: "dateType",
      field: "birthDate",
      filter: "agDateColumnFilter",
      valueGetter: (params) => params.data?.socSec && socSecToBirthDate(params.data.socSec),
      valueFormatter: dateFormatter,
    },
    {
      headerName: t("Birthday", "Syntymäpäivä"),
      cellClass: "dateType",
      field: "nextBirthDay",
      filter: "agDateColumnFilter",
      valueGetter: (params) =>
        params.data?.socSec && nextBirthDay(new Date(targetDate), socSecToBirthDate(params.data.socSec)),
      valueFormatter: dateFormatter,
    },
    {
      headerName: t("Age", "Ikä"),
      field: "age",
      filter: "agNumberColumnFilter",
      valueGetter: (params) => params.data && calculateAge(socSecToBirthDate(params.data.socSec)),
    },
  ];

  const initialState = {
    columnVisibility: {
      hiddenColIds: [],
    },
  };

  const getContextMenuItems = useCallback((params) => {
    let items = [];

    items.push({
      name: t("DefaultGridValues", "Palauta taulukon oletusasetukset"),
      action: function () {
        restoreViewState(viewStateKey, initialState);
      },
      icon: restoreIconString,
    });

    items.push("separator");

    items = items.concat(
      contextMenu(t, params, setReport, {
        title: t("Anniversaries", "Merkkipäivät"),
        companyname: companyName,
        targetdate: new Date(targetDate).toLocaleDateString(localized.resolvedLanguage),
      }),
    );
    return items;
  }, [t, companyName, targetDate, localized.resolvedLanguage]);

  return (
    <>
      {report !== null && <PdfReport json={report} onCloseReport={closeReport} />}

      <Row className="my-1">
        <Col sm="3">
          <h2 className="viewheading">{t("Anniversaries", "Merkkipäivät")}</h2>
        </Col>
        <Col sm="6">
          <Form.Group as={Row} controlId="targetDate">
            <Col sm="4">
              <DefaultDatePicker
                id={`${prefix}.targetDate`}
                name="targetDate"
                label={t("FromDate")}
                value={targetDate}
                onChange={(iso) => setTargetDate(iso ?? localDate())}
              />
            </Col>
          </Form.Group>
        </Col>
        <Col sm="2" align="right"></Col>
      </Row>

      <Row className="ag-theme-material styles-ag flex-grow-1 mt-3">
        <AgGridReact
          {...gridSettings}
          loading={!persons}
          ref={gridRef}
          components={gridComponents}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          customChartThemes={customChartThemes}
          chartThemes={["chartTheme"]}
          sideBar={sideBar}
          rowData={persons ?? []}
          getContextMenuItems={getContextMenuItems}
          statusBar={statusBar}
          getRowId={(params) => String(params.data.personId)}
          getLocaleText={(params) => getLocaleText(t, params)}
          animateRows
          suppressCellFocus
          enableCellTextSelection
          initialState={loadViewState(viewStateKey) || initialState}
          onStateUpdated={({ state }) => saveViewState(state, viewStateKey)}
          debug={import.meta.env.DEV}
        />
      </Row>
    </>
  );
}
